import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "chatArea", "responseArea", "input"]
  static values = {
    thanksText: String,
    loadingText: String,
    queryTemplate: String
  }

  connect() {
    this.conversationHistory = []

    // Turbo Streamでai-resultに追加された後にスクロール & レスポンス取得
    this.observer = new MutationObserver((mutations) => {
      this.scrollToLastUserMessage()
      this.captureAiResponse(mutations)
    })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  open() {
    this.modalTarget.classList.add("active")
    this.lockScroll()
    this.boundKeydown = this.keydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)

    // ai-resultの変更を監視してスクロール
    const aiResult = document.getElementById("ai-result")
    if (aiResult && this.observer) {
      this.observer.observe(aiResult, { childList: true, subtree: true })
    }
  }

  close() {
    this.modalTarget.classList.remove("active")
    this._clearHiddenStyles()
    this.unlockScroll()
    if (this.boundKeydown) {
      document.removeEventListener("keydown", this.boundKeydown)
    }
    if (this.observer) this.observer.disconnect()
    this.conversationHistory = []
  }

  // おすすめカードタップ時: AIチャットを裏に隠す（閉じない）
  hide() {
    this.modalTarget.style.opacity = "0"
    this.modalTarget.style.pointerEvents = "none"
    this.modalTarget.style.zIndex = "50"
  }

  // アイテム詳細モーダルを閉じた時: AIチャットを再表示
  restore() {
    this._clearHiddenStyles()
  }

  _clearHiddenStyles() {
    this.modalTarget.style.opacity = ""
    this.modalTarget.style.pointerEvents = ""
    this.modalTarget.style.zIndex = ""
  }

  // iOS Safari対応: overflow:hiddenだけでは背景スクロールが止まらない
  lockScroll() {
    this.savedScrollY = window.scrollY
    document.body.style.position = "fixed"
    document.body.style.top = `-${this.savedScrollY}px`
    document.body.style.left = "0"
    document.body.style.right = "0"
    document.body.style.overflow = "hidden"
  }

  unlockScroll() {
    document.body.style.position = ""
    document.body.style.top = ""
    document.body.style.left = ""
    document.body.style.right = ""
    document.body.style.overflow = ""
    window.scrollTo(0, this.savedScrollY || 0)
  }

  clickOverlay(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  submit(event) {
    if (event) event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (!query) return
    this.inputTarget.value = ""
    this.sendQuery(query)
  }

  inputKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.submit()
    }
  }

  openWithReviewContext(itemName, comment, itemId) {
    this.open()

    const aiResult = document.getElementById("ai-result")
    if (!aiResult) return

    // お礼バブルを表示（スクロール先マーカー付き）
    const thanksBubble = document.createElement("div")
    thanksBubble.className = "ai-bubble mb-6"
    thanksBubble.dataset.userMessage = "true"
    thanksBubble.style.animation = "menu-fade-in 0.6s ease-out forwards"
    thanksBubble.innerHTML = `<p class="text-sm leading-relaxed">${this.escapeHtml(this.thanksTextValue)}</p>`
    aiResult.appendChild(thanksBubble)

    // ローディング表示
    const loading = document.createElement("div")
    loading.id = "ai-loading-indicator"
    loading.className = "ai-bubble mb-6"
    loading.style.animation = "menu-fade-in 2s infinite alternate"
    loading.innerHTML = `<p class="text-xs italic tracking-widest text-[#8C8C8C]">${this.escapeHtml(this.loadingTextValue)}</p>`
    aiResult.appendChild(loading)
    this.scrollToBottom()

    // AI推薦フォームにレビュー情報をセットして送信
    const form = this.element.querySelector("form[data-ai-chat-form]")
    if (form) {
      const queryInput = form.querySelector("input[name='query']")
      const reviewItemIdInput = form.querySelector("input[name='reviewed_item_id']")
      const reviewCommentInput = form.querySelector("input[name='review_comment']")
      const historyInput = form.querySelector("input[name='conversation_history']")

      if (queryInput) queryInput.value = this.queryTemplateValue.replace("%{item_name}", itemName)
      if (reviewItemIdInput) reviewItemIdInput.value = itemId
      if (reviewCommentInput) reviewCommentInput.value = comment
      if (historyInput) historyInput.value = JSON.stringify(this.conversationHistory)

      form.requestSubmit()

      // 送信後にhidden fieldsをクリア
      setTimeout(() => {
        if (reviewItemIdInput) reviewItemIdInput.value = ""
        if (reviewCommentInput) reviewCommentInput.value = ""
      }, 100)
    }
  }

  quickAsk(event) {
    const query = event.currentTarget.dataset.query
    if (query) this.sendQuery(query)
  }

  sendQuery(query) {
    // ai-resultフレームに追加して正しい順序を保つ
    const aiResult = document.getElementById("ai-result")
    if (!aiResult) return

    // 会話履歴にユーザーメッセージを追加
    this.conversationHistory.push({ role: "user", content: query })
    // 直近10往復（20メッセージ）に制限
    if (this.conversationHistory.length > 20) {
      this.conversationHistory = this.conversationHistory.slice(-20)
    }

    // ユーザーバブルを表示
    const userMsg = document.createElement("div")
    userMsg.className = "flex justify-end mb-6"
    userMsg.dataset.userMessage = "true"
    userMsg.style.animation = "menu-fade-in 0.6s ease-out forwards"
    userMsg.innerHTML = `<div class="bg-[#2C2C2C] text-white rounded-2xl px-5 py-3 text-sm font-medium">${this.escapeHtml(query)}</div>`
    aiResult.appendChild(userMsg)

    // ローディング表示
    const loading = document.createElement("div")
    loading.className = "ai-bubble mb-6"
    loading.style.animation = "menu-fade-in 2s infinite alternate"
    loading.innerHTML = `<p class="text-xs italic tracking-widest text-[#8C8C8C]">${this.escapeHtml(this.loadingTextValue)}</p>`
    aiResult.appendChild(loading)
    this.scrollToBottom()

    // フォームを送信
    const form = this.element.querySelector("form[data-ai-chat-form]")
    if (form) {
      const queryInput = form.querySelector("input[name='query']")
      const historyInput = form.querySelector("input[name='conversation_history']")
      if (queryInput) {
        queryInput.value = query
        if (historyInput) historyInput.value = JSON.stringify(this.conversationHistory.slice(0, -1))
        loading.id = "ai-loading-indicator"
        form.requestSubmit()
      }
    }
  }

  scrollToBottom() {
    if (this.hasChatAreaTarget) {
      this.chatAreaTarget.scrollTo({ top: this.chatAreaTarget.scrollHeight, behavior: "smooth" })
    }
  }

  scrollToLastUserMessage() {
    if (!this.hasChatAreaTarget) return
    const aiResult = document.getElementById("ai-result")
    if (!aiResult) return

    // 最後のユーザーメッセージを探す
    const userMessages = aiResult.querySelectorAll("[data-user-message]")
    const lastUserMsg = userMessages[userMessages.length - 1]
    if (lastUserMsg) {
      // ユーザーメッセージがチャットエリア上部に来る位置にスクロール
      const chatArea = this.chatAreaTarget
      const msgTop = lastUserMsg.offsetTop - chatArea.offsetTop
      chatArea.scrollTo({ top: msgTop - 16, behavior: "smooth" })
    } else {
      this.scrollToBottom()
    }
  }

  captureAiResponse(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        // 追加されたノード自体、またはその子要素から data-ai-response-text を探す
        const el = node.dataset?.aiResponseText !== undefined ? node : node.querySelector?.("[data-ai-response-text]")
        if (el) {
          const text = el.dataset.aiResponseText
          if (text) {
            this.conversationHistory.push({ role: "assistant", content: text })
            if (this.conversationHistory.length > 20) {
              this.conversationHistory = this.conversationHistory.slice(-20)
            }
          }
          el.remove()
        }
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
